import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { settingsApi as S } from "@/lib/settings";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Building, ListTree, Hash, SlidersHorizontal, Save } from "lucide-react";

export default function Settings() {
  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Settings &amp; Masters</h2>
        <p className="mt-1 text-sm text-slate-500">Centralized company profile, reusable master data, number series and ERP preferences.</p>
      </div>
      <Tabs defaultValue="company">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="company" data-testid="tab-company"><Building className="mr-1.5 h-4 w-4" />Company</TabsTrigger>
          <TabsTrigger value="masters" data-testid="tab-masters"><ListTree className="mr-1.5 h-4 w-4" />Masters</TabsTrigger>
          <TabsTrigger value="series" data-testid="tab-series"><Hash className="mr-1.5 h-4 w-4" />Number Series</TabsTrigger>
          <TabsTrigger value="prefs" data-testid="tab-prefs"><SlidersHorizontal className="mr-1.5 h-4 w-4" />Preferences</TabsTrigger>
        </TabsList>
        <TabsContent value="company" className="mt-4"><Company /></TabsContent>
        <TabsContent value="masters" className="mt-4"><Masters /></TabsContent>
        <TabsContent value="series" className="mt-4"><NumberSeries /></TabsContent>
        <TabsContent value="prefs" className="mt-4"><Preferences /></TabsContent>
      </Tabs>
    </div>
  );
}

const Spin = () => <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

function Company() {
  const [f, setF] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { S.getCompany().then((d) => setF(d || {})).finally(() => setLoading(false)); }, []);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { setBusy(true); try { await S.updateCompany(f); toast.success("Company profile saved"); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); } };
  if (loading) return <Spin />;
  const FIELDS = [["name", "Company name"], ["legal_name", "Legal name"], ["gstin", "GSTIN"], ["pan", "PAN"], ["state", "State"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"]];
  return (
    <Card className="p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {FIELDS.map(([k, l]) => (
          <div key={k} className="space-y-1.5"><Label className="text-xs">{l}</Label><Input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`company-${k}`} /></div>
        ))}
        <div className="md:col-span-2 space-y-1.5"><Label className="text-xs">Registered address</Label><Textarea rows={2} value={f.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="company-address" /></div>
      </div>
      <div className="mt-5"><Button onClick={save} disabled={busy} data-testid="save-company-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" />Save</>}</Button></div>
    </Card>
  );
}

function Masters() {
  const { can } = useAuth();
  const canEdit = can("settings", "edit");
  const canDelete = can("settings", "delete");
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("unit");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "" });

  const current = types.find((t) => t.type === type);
  useEffect(() => { S.meta().then((m) => setTypes(m.master_types)); }, []);
  const load = () => { setLoading(true); S.listMasters(type).then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); setForm({ name: "" }); /* eslint-disable-next-line */ }, [type]);

  const add = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try { await S.createMaster(type, form); toast.success("Added"); setForm({ name: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this entry?")) return; try { await S.deleteMaster(id); toast.success("Deleted"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };

  return (
    <Card className="p-4">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1"><Label className="text-xs">Master type</Label>
          <Select value={type} onValueChange={setType}><SelectTrigger className="w-56" data-testid="master-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t.type} value={t.type}>{t.label}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      {canEdit && (
        <div className="mb-4 flex flex-wrap items-end gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-48" data-testid="master-name-input" /></div>
          {(current?.fields || []).map((fld) => (
            <div key={fld} className="space-y-1"><Label className="text-xs capitalize">{fld.replace("_", " ")}</Label>
              <Input type={fld === "date" ? "date" : fld === "rate" ? "number" : "text"} value={form[fld] || ""} onChange={(e) => setForm({ ...form, [fld]: e.target.value })} className="w-40" data-testid={`master-${fld}-input`} /></div>
          ))}
          <Button onClick={add} data-testid="add-master-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Plus className="mr-1 h-4 w-4" />Add</Button>
        </div>
      )}
      {loading ? <Spin /> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs uppercase text-slate-500">Name</TableHead>{(current?.fields || []).map((fl) => <TableHead key={fl} className="text-xs uppercase text-slate-500 capitalize">{fl.replace("_", " ")}</TableHead>)}<TableHead className="text-xs uppercase text-slate-500">Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`master-row-${r.id}`}>
                  <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
                  {(current?.fields || []).map((fl) => <TableCell key={fl} className="text-slate-600">{r[fl] || "—"}</TableCell>)}
                  <TableCell><Badge className={r.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-slate-100 text-slate-500 hover:bg-slate-100"}>{r.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                  <TableCell className="text-right">{canDelete && <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => del(r.id)} data-testid={`delete-master-${r.id}`}><Trash2 className="h-4 w-4" /></Button>}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={4} className="py-10 text-center text-slate-400" data-testid="masters-empty">No entries yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}

function NumberSeries() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { S.numberSeries().then(setRows).finally(() => setLoading(false)); }, []);
  if (loading) return <Spin />;
  return (
    <Card className="p-4">
      <p className="mb-3 text-sm text-slate-500">Auto-generated document numbers per module (read-only current counter).</p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead className="text-xs uppercase text-slate-500">Series</TableHead><TableHead className="text-xs uppercase text-slate-500">Current No.</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((r) => <TableRow key={r.key} data-testid={`series-${r.key}`}><TableCell className="font-medium text-slate-800">{r.label}</TableCell><TableCell className="tabular-nums text-slate-700">{r.current}</TableCell></TableRow>)}</TableBody>
        </Table>
      </div>
    </Card>
  );
}

function Preferences() {
  const [f, setF] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { S.getPreferences().then((d) => setF(d || {})).finally(() => setLoading(false)); }, []);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { setBusy(true); try { await S.updatePreferences(f); toast.success("Preferences saved"); } catch (e) { toast.error("Error"); } finally { setBusy(false); } };
  if (loading) return <Spin />;
  return (
    <Card className="p-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1.5"><Label className="text-xs">Currency</Label><Input value={f.currency || ""} onChange={(e) => set("currency", e.target.value)} placeholder="INR" data-testid="pref-currency" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Financial year start (MM-DD)</Label><Input value={f.fy_start || ""} onChange={(e) => set("fy_start", e.target.value)} placeholder="04-01" data-testid="pref-fy" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Default tax type</Label>
          <Select value={f.default_tax_type || "GST"} onValueChange={(v) => set("default_tax_type", v)}><SelectTrigger data-testid="pref-tax"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="GST">GST</SelectItem><SelectItem value="IGST">IGST</SelectItem><SelectItem value="None">None</SelectItem></SelectContent></Select>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Home state (for GST)</Label><Input value={f.home_state || ""} onChange={(e) => set("home_state", e.target.value)} placeholder="Gujarat" data-testid="pref-state" /></div>
      </div>
      <div className="mt-5"><Button onClick={save} disabled={busy} data-testid="save-prefs-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" />Save</>}</Button></div>
    </Card>
  );
}
